import {
  Add,
  Analytics,
  Assignment,
  Business,
  Category,
  ContactSupport,
  Dashboard,
  ExpandLess,
  ExpandMore,
  FactCheck,
  Forum,
  History,
  Logout,
  AccountCircle,
  People,
  Person,
  PlaylistAddCheck,
  Settings,
  ShortText,
  Store,
  Groups,
  FolderShared,
  AdminPanelSettings,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/slices/authSlice";

const drawerWidth = 240;

const canSee = (permissions, permission) => {
  if (!permission) {
    return true;
  }
  if (!permissions || permissions.length === 0) {
    return ["view_dashboard", "view_tickets"].includes(permission);
  }
  return permissions.includes(permission);
};

const Sidebar = ({ open, onClose, onNavigate, onLogout }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const dispatch = useDispatch();
  const { permissions } = useSelector((state) => state.auth);
  const [openGroups, setOpenGroups] = useState({});

  const menuStructure = useMemo(() => {
    const link = (text, icon, path, permission) => ({
      kind: "link",
      text,
      icon,
      path,
      permission,
    });

    return [
      link(t("sidebar.home"), <Dashboard />, "/home", "view_dashboard"),
      link(
        t("common.createTicket"),
        <Add />,
        "/create-ticket",
        "create_tickets"
      ),
      link(t("sidebar.tickets"), <Assignment />, "/tickets", "view_tickets"),
      link(t("sidebar.reports"), <Analytics />, "/reports", "view_reports"),
      link(
        t("sidebar.actionsReport"),
        <FactCheck />,
        "/actions-report",
        "view_actions_report"
      ),
      {
        kind: "group",
        id: "masterData",
        text: t("sidebar.masterData"),
        icon: <FolderShared />,
        children: [
          link(
            t("sidebar.customers"),
            <People />,
            "/customers",
            "manage_customers"
          ),
          link(
            t("sidebar.communicationMethods"),
            <Forum />,
            "/communication-methods",
            "manage_communication_methods"
          ),
          link(
            t("sidebar.contactReasons"),
            <ContactSupport />,
            "/contact-reasons",
            "manage_contact_reasons"
          ),
          link(
            t("sidebar.shortSubjects"),
            <ShortText />,
            "/short-subjects",
            "manage_short_subjects"
          ),
          link(
            t("sidebar.branches"),
            <Store />,
            "/branches",
            "manage_branches"
          ),
          link(
            t("sidebar.departments"),
            <Business />,
            "/departments",
            "manage_departments"
          ),
          link(
            t("sidebar.products"),
            <Category />,
            "/products",
            "manage_products"
          ),
        ],
      },
      {
        kind: "group",
        id: "administration",
        text: t("sidebar.administration"),
        icon: <AdminPanelSettings />,
        children: [
          link(
            t("sidebar.actionTypes"),
            <PlaylistAddCheck />,
            "/action-types",
            "manage_action_types"
          ),
          link(
            t("sidebar.employees"),
            <Person />,
            "/employees",
            "manage_employees"
          ),
          link(t("sidebar.users"), <Groups />, "/users", "manage_users"),
          link(
            t("sidebar.activityLog"),
            <History />,
            "/activity-log",
            "view_activity_log"
          ),
        ],
      },
      link(t("navbar.profile"), <AccountCircle />, "/profile", null),
      link(t("sidebar.settings"), <Settings />, "/settings", "view_settings"),
      {
        kind: "action",
        id: "logout",
        text: t("navbar.logout"),
        icon: <Logout />,
      },
    ];
  }, [t]);

  const visibleMenu = useMemo(() => {
    return menuStructure
      .map((entry) => {
        if (entry.kind === "group") {
          const children = entry.children.filter((child) =>
            canSee(permissions, child.permission)
          );
          if (children.length === 0) {
            return null;
          }
          return { ...entry, children };
        }
        if (entry.kind === "link") {
          return canSee(permissions, entry.permission) ? entry : null;
        }
        return entry;
      })
      .filter(Boolean);
  }, [menuStructure, permissions]);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleItemClick = (path) => {
    if (onNavigate) {
      onNavigate(path);
    }
    onClose();
  };

  const handleLogout = () => {
    onClose();
    if (onLogout) {
      onLogout();
    }
    dispatch(logoutUser());
  };

  const renderLinkButton = (item, nested = false) => (
    <ListItem key={item.path} disablePadding>
      <ListItemButton
        onClick={() => handleItemClick(item.path)}
        sx={{ pl: nested ? 4 : 2 }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    </ListItem>
  );

  const drawer = (
    <Box>
      <Box sx={{ p: 2, pt: "80px", textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          {t("navbar.title")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("login.subtitle")}
        </Typography>
      </Box>
      <Divider />
      <List component="nav">
        {visibleMenu.map((entry) => {
          if (entry.kind === "link") {
            return renderLinkButton(entry);
          }

          if (entry.kind === "action") {
            return (
              <ListItem key={entry.id} disablePadding>
                <ListItemButton onClick={handleLogout}>
                  <ListItemIcon sx={{ minWidth: 40 }}>{entry.icon}</ListItemIcon>
                  <ListItemText primary={entry.text} />
                </ListItemButton>
              </ListItem>
            );
          }

          const isOpen = Boolean(openGroups[entry.id]);
          return (
            <React.Fragment key={entry.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => toggleGroup(entry.id)}>
                  <ListItemIcon sx={{ minWidth: 40 }}>{entry.icon}</ListItemIcon>
                  <ListItemText primary={entry.text} />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {entry.children.map((child) => renderLinkButton(child, true))}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        display: { xs: "block", sm: "block" },
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: drawerWidth,
          direction: isRTL ? "rtl" : "ltr",
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default Sidebar;
